import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { 
  getCategories, 
  getCategoriesByType,
  createCategorie, 
  updateCategorie, 
  deleteCategorie,
  Categorie 
} from '../lib/services/CategoryService';

interface UseCategoriesReturn {
  categories: Categorie[];
  loading: boolean;
  error: string | null;
  refreshCategories: () => Promise<void>;
  addCategory: (category: Categorie) => Promise<{ success: boolean; error?: string }>;
  editCategory: (id: string, updates: Partial<Categorie>) => Promise<{ success: boolean; error?: string }>;
  removeCategory: (id: string) => Promise<{ success: boolean; error?: string }>;
  getCategoriesByType: (type: 'depense' | 'revenu') => Promise<Categorie[]>;
}

export const useCategories = (): UseCategoriesReturn => {
  const { user } = useAuth();
  const { impersonatedUserId } = useImpersonation();
  const [categories, setCategories] = useState<Categorie[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('useCategories - Récupération des catégories...');
      
      const { data, error: fetchError } = await getCategories();
      
      if (fetchError) {
        console.error('useCategories - Erreur lors de la récupération:', fetchError);
        setError(fetchError.message || 'Erreur lors de la récupération des catégories');
        return;
      }

      if (data) {
        console.log('useCategories - Catégories récupérées:', data.length);
        setCategories(data);
      } else {
        console.log('useCategories - Aucune catégorie trouvée');
        setCategories([]);
      }
    } catch (err) {
      console.error('useCategories - Exception:', err);
      setError(err instanceof Error ? err.message : 'Erreur inattendue');
    } finally {
      setLoading(false);
    }
  };

  const refreshCategories = async () => {
    await fetchCategories();
  };

  const addCategory = async (category: Categorie): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log('useCategories - Ajout d\'une catégorie:', category);
      
      const { data, error: createError } = await createCategorie(category);
      
      if (createError) {
        console.error('useCategories - Erreur lors de la création:', createError);
        return { success: false, error: createError.message || 'Erreur lors de la création' };
      }

      if (data) {
        console.log('useCategories - Catégorie créée avec succès');
        await fetchCategories();
        return { success: true };
      }

      return { success: false, error: 'Erreur lors de la création' };
    } catch (err) {
      console.error('useCategories - Exception lors de la création:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Erreur inattendue' };
    }
  };

  const editCategory = async (id: string, updates: Partial<Categorie>): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log('useCategories - Modification de la catégorie:', id, updates);
      
      const { data, error: updateError } = await updateCategorie(id, updates);
      
      if (updateError) {
        console.error('useCategories - Erreur lors de la modification:', updateError);
        return { success: false, error: updateError.message || 'Erreur lors de la modification' };
      }

      if (data) {
        console.log('useCategories - Catégorie modifiée avec succès');
        await fetchCategories();
        return { success: true };
      }

      return { success: false, error: 'Erreur lors de la modification' };
    } catch (err) {
      console.error('useCategories - Exception lors de la modification:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Erreur inattendue' };
    }
  };

  const removeCategory = async (id: string): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log('useCategories - Suppression de la catégorie:', id);
      
      const { success, error: deleteError } = await deleteCategorie(id);
      
      if (!success) {
        console.error('useCategories - Erreur lors de la suppression:', deleteError);
        return { success: false, error: deleteError?.message || 'Erreur lors de la suppression' };
      }

      console.log('useCategories - Catégorie supprimée avec succès');
      await fetchCategories();
      return { success: true };
    } catch (err) {
      console.error('useCategories - Exception lors de la suppression:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Erreur inattendue' };
    }
  };

  const fetchCategoriesByType = async (type: 'depense' | 'revenu'): Promise<Categorie[]> => {
    try {
      const { data, error } = await getCategoriesByType(type);
      if (error) {
        console.error('useCategories - Erreur lors de la récupération par type:', error);
        return [];
      }
      return data || [];
    } catch (err) {
      console.error('useCategories - Exception lors de la récupération par type:', err);
      return [];
    }
  };

  useEffect(() => {
    if (user) {
      fetchCategories();
    } else {
      setCategories([]);
      setLoading(false);
    }
  }, [user, impersonatedUserId]);

  return {
    categories,
    loading,
    error,
    refreshCategories,
    addCategory,
    editCategory,
    removeCategory,
    getCategoriesByType: fetchCategoriesByType
  };
}; 